/**
 * impersonation.ts — admin "Login as user" (from the Users grid).
 *
 * start(): asks the API for a time-limited session AS the user, parks the admin's own session in
 * localStorage (IMPERSONATION_KEY) and reloads into the user's view. stop() puts the admin's session back.
 * While active, MainLayout shows a "Viewing as ..." banner. The server enforces everything that matters
 * (admin-only, no admin targets, hard 60-minute end, audit log); this file only swaps the stored session.
 */

const TOKEN_KEY = 'authToken';
const USER_KEY = 'aibuddy_user';
const SESSION_KEY = 'aibuddy_session';
export const IMPERSONATION_KEY = 'aibuddy_impersonation';
/** Where an admin lands after returning to their own account. */
export const IMPERSONATION_RETURN_PATH = '/users';

export interface ImpersonationState {
  /** the admin's own session, restored on stop() */
  origin: { token: string; user: string; session: string | null };
  target: { id: string; name: string; email: string };
  admin_email?: string | null;
  ends_at: string;
}

export function getImpersonation(): ImpersonationState | null {
  try {
    const raw = localStorage.getItem(IMPERSONATION_KEY);
    return raw ? (JSON.parse(raw) as ImpersonationState) : null;
  } catch {
    return null;
  }
}

export const isImpersonating = (): boolean => getImpersonation() !== null;

/** Sign in as `userId`. Resolves just before the page reloads into that user's dashboard. */
export async function startImpersonation(userId: string): Promise<void> {
  if (isImpersonating()) throw new Error('Return to your own account first.');
  const token = localStorage.getItem(TOKEN_KEY);
  const user = localStorage.getItem(USER_KEY);
  if (!token || !user) throw new Error('You are not signed in.');

  // Loaded on demand: the API client itself imports this module (restoreOriginSession) for its 401 handling.
  const { apiClient } = await import('@services/api/client');
  const response = await apiClient.post(`/api/v1/users/${userId}/impersonate`);
  const data = response.data?.data;
  if (!data?.accessToken || !data?.user) throw new Error('Could not sign in as this user.');

  const target = data.user;
  const state: ImpersonationState = {
    origin: { token, user, session: localStorage.getItem(SESSION_KEY) },
    target: {
      id: target.id,
      name: `${target.first_name || ''} ${target.last_name || ''}`.trim() || target.email,
      email: target.email,
    },
    admin_email: data.impersonation?.impersonator_email ?? null,
    ends_at: data.impersonation?.ends_at,
  };
  localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(state));
  localStorage.setItem(TOKEN_KEY, data.accessToken);
  localStorage.setItem(USER_KEY, JSON.stringify({ ...target }));
  localStorage.removeItem(SESSION_KEY);
  // Full reload: every context (auth, entitlements, onboarding cache, sidebar) must rebuild as the user.
  window.location.href = '/dashboard';
}

/**
 * Put the admin's own session back. Returns false when there was nothing to restore.
 * Safe to call from the API client's 401 handling (no network, no React).
 */
export function restoreOriginSession(): boolean {
  const state = getImpersonation();
  localStorage.removeItem(IMPERSONATION_KEY);
  if (!state?.origin?.token || !state.origin.user) return false;
  localStorage.setItem(TOKEN_KEY, state.origin.token);
  localStorage.setItem(USER_KEY, state.origin.user);
  if (state.origin.session) localStorage.setItem(SESSION_KEY, state.origin.session);
  else localStorage.removeItem(SESSION_KEY);
  return true;
}

/** Leave the user's view and go back to the admin's Users page. */
export function stopImpersonation(): void {
  const restored = restoreOriginSession();
  window.location.href = restored ? IMPERSONATION_RETURN_PATH : '/auth/login';
}
